import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Ticket, CheckCircle, Clock, XCircle, AlertTriangle, TrendingUp, DollarSign, CalendarDays } from "lucide-react";
import { buildYearOptions } from "@/lib/appointmentYears";

const PIE_COLORS = ["#0f172a", "#dc2626", "#d97706", "#059669", "#7c3aed"];

export default function VoucherDashboard() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [center, setCenter] = useState("");
  const [centers, setCenters] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ period });
      if (center) params.append("center", center);
      if (selectedYear) params.append("year", selectedYear.toString());
      const r = await axiosAuth().get(`/vouchers/dashboard?${params}`);
      setData(r.data);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(e?.response?.data?.detail || "Error al cargar dashboard");
      }
    }
    finally { setLoading(false); }
  }, [axiosAuth, period, center, selectedYear]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axiosAuth().get("/vouchers/config").then(r => setCenters(r.data.centers || [])).catch(() => {});
  }, [axiosAuth]);

  if (loading) return <div className="p-8 text-center text-sm text-slate-400">Cargando dashboard...</div>;
  if (forbidden) return (
    <div className="p-8 text-center" data-testid="voucher-dashboard-forbidden">
      <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
      <p className="text-sm font-semibold text-slate-800">Sin permisos para ver este dashboard</p>
      <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
        Tu rol no tiene el permiso <code className="bg-slate-100 px-1 rounded">ver</code> sobre el módulo <code className="bg-slate-100 px-1 rounded">atenciones_comerciales</code>.
        Pide a un administrador que lo añada.
      </p>
    </div>
  );
  if (!data) return <div className="p-8 text-center text-sm text-slate-400">Sin datos.</div>;

  const { kpis, monthly_chart, by_type, top_issuers, top_centers, expiring_soon, recent_issued, recent_redeemed } = data;

  const kpiCards = [
    { label: "Emitidos hoy", value: kpis.emitted_today, icon: Ticket, accent: "#2563eb", bg: "bg-blue-50" },
    { label: "Emitidos mes", value: kpis.emitted_month, icon: CalendarDays, accent: "#0f172a", bg: "bg-slate-50" },
    { label: "Canjeados mes", value: kpis.redeemed_month, icon: CheckCircle, accent: "#059669", bg: "bg-emerald-50" },
    { label: "Pendientes", value: kpis.pending, icon: Clock, accent: "#d97706", bg: "bg-amber-50" },
    { label: "Caducados", value: kpis.expired, icon: AlertTriangle, accent: "#ea580c", bg: "bg-orange-50" },
    { label: "Anulados", value: kpis.cancelled, icon: XCircle, accent: "#dc2626", bg: "bg-red-50" },
    { label: "Ratio canje", value: `${kpis.redeem_ratio}%`, icon: TrendingUp, accent: "#7c3aed", bg: "bg-violet-50" },
    { label: "Valor mes", value: `${kpis.estimated_value_month.toLocaleString("es-ES")}€`, icon: DollarSign, accent: "#0f172a", bg: "bg-slate-50" },
  ];

  const statusLabel = (s) => ({ borrador: "Borrador", emitido: "Emitido", enviado: "Enviado", entregado: "Entregado", canjeado: "Canjeado", caducado: "Caducado", anulado: "Anulado" }[s] || s);
  const statusDot = (s) => ({ emitido: "bg-blue-500", enviado: "bg-violet-500", entregado: "bg-cyan-500", canjeado: "bg-emerald-500", caducado: "bg-amber-500", anulado: "bg-red-500" }[s] || "bg-slate-400");

  return (
    <div className="space-y-6" data-testid="voucher-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase tracking-tight">Atenciones Comerciales</h1>
          <p className="text-sm text-slate-500 mt-1">Control y seguimiento de bonos emitidos</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedYear.toString()} onValueChange={v => { setSelectedYear(parseInt(v)); setLoading(true); }}>
            <SelectTrigger className="w-[90px] rounded-sm h-9 text-xs border-slate-200 font-semibold font-mono" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {buildYearOptions().map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={v => { setPeriod(v); setLoading(true); }}>
            <SelectTrigger className="w-[130px] rounded-sm h-9 text-xs border-slate-200 font-semibold" data-testid="period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hoy</SelectItem>
              <SelectItem value="month">Este mes</SelectItem>
              <SelectItem value="quarter">Trimestre</SelectItem>
              <SelectItem value="year">Año completo</SelectItem>
            </SelectContent>
          </Select>
          {centers.length > 0 && (
            <Select value={center || "all"} onValueChange={v => { setCenter(v === "all" ? "" : v); setLoading(true); }}>
              <SelectTrigger className="w-[150px] rounded-sm h-9 text-xs border-slate-200 font-semibold"><SelectValue placeholder="Centro" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los centros</SelectItem>
                {centers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="kpi-grid">
        {kpiCards.map(k => (
          <Card key={k.label} className="border-slate-200 rounded-sm hover:border-slate-300 transition-colors">
            <CardContent className="p-4 text-center">
              <div className={`w-9 h-9 rounded-sm ${k.bg} flex items-center justify-center mx-auto mb-2`}>
                <k.icon className="w-4.5 h-4.5" style={{ color: k.accent }} />
              </div>
              <p className="text-xl font-bold text-slate-900 leading-tight">{k.value}</p>
              <p className="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-bold mt-1">{k.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Monthly chart */}
        <Card className="border-slate-200 rounded-sm lg:col-span-2">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-4">Emitidos vs Canjeados por mes</p>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={monthly_chart} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4, border: "1px solid #e2e8f0" }} />
                <Bar dataKey="emitidos" fill="#0f172a" radius={[3, 3, 0, 0]} name="Emitidos" />
                <Bar dataKey="canjeados" fill="#059669" radius={[3, 3, 0, 0]} name="Canjeados" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* By type */}
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-4">Distribucion por tipo</p>
            {by_type.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={140}>
                  <PieChart>
                    <Pie data={by_type} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={55} innerRadius={30} strokeWidth={2} stroke="#fff">
                      {by_type.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 mt-3">
                  {by_type.map((t, i) => (
                    <div key={t.type} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="text-slate-600">{t.name}</span>
                      </span>
                      <span className="font-bold text-slate-800 font-mono">{t.count}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : <p className="text-xs text-slate-400 text-center py-8">Sin datos</p>}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Top issuers */}
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-4">Top emisores</p>
            {top_issuers.length > 0 ? top_issuers.map((iss, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                <span className="text-xs text-slate-700 font-medium">{iss.name}</span>
                <Badge variant="outline" className="h-5 text-[10px] rounded-sm border-slate-200 font-bold font-mono">{iss.count}</Badge>
              </div>
            )) : <p className="text-xs text-slate-400 text-center py-4">Sin datos</p>}
          </CardContent>
        </Card>

        {/* Top centers */}
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-4">Top centros</p>
            {top_centers.length > 0 ? top_centers.map((c, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                <span className="text-xs text-slate-700 font-medium">{c.name}</span>
                <Badge variant="outline" className="h-5 text-[10px] rounded-sm border-slate-200 font-bold font-mono">{c.count}</Badge>
              </div>
            )) : <p className="text-xs text-slate-400 text-center py-4">Sin datos</p>}
          </CardContent>
        </Card>

        {/* Expiring soon */}
        <Card className="border-amber-200/60 rounded-sm">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-amber-600 mb-4 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Proximos a caducar (7 dias)
            </p>
            {expiring_soon.length > 0 ? expiring_soon.map(v => (
              <div key={v.id} className="py-2.5 border-b border-amber-100/80 last:border-0 cursor-pointer hover:bg-amber-50/30 transition-colors -mx-2 px-2 rounded-sm"
                onClick={() => navigate(`/admin/atenciones/detalle/${v.id}`)}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono font-bold text-slate-700">{v.code}</span>
                  <span className="text-[10px] text-amber-600 font-bold">{v.expiry_date}</span>
                </div>
                <p className="text-[10px] text-slate-500 mt-0.5">{v.client_name} — {v.plate}</p>
              </div>
            )) : <p className="text-xs text-slate-400 text-center py-4">Ningun bono proximo a caducar</p>}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Recent issued */}
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-4">Ultimos emitidos</p>
            {recent_issued.length > 0 ? recent_issued.map(v => (
              <div key={v.id} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50/80 -mx-2 px-2 rounded-sm transition-colors"
                onClick={() => navigate(`/admin/atenciones/detalle/${v.id}`)}>
                <div>
                  <span className="text-xs font-mono font-bold text-slate-800">{v.code}</span>
                  <p className="text-[10px] text-slate-500 mt-0.5">{v.client_name} — {v.plate} — {v.center}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${statusDot(v.status)}`} />
                  <span className="text-[10px] text-slate-400 font-medium">{statusLabel(v.status)}</span>
                </div>
              </div>
            )) : <p className="text-xs text-slate-400 text-center py-4">Sin datos</p>}
          </CardContent>
        </Card>

        {/* Recent redeemed */}
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-4">Ultimos canjeados</p>
            {recent_redeemed.length > 0 ? recent_redeemed.map(v => (
              <div key={v.id} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50/80 -mx-2 px-2 rounded-sm transition-colors"
                onClick={() => navigate(`/admin/atenciones/detalle/${v.id}`)}>
                <div>
                  <span className="text-xs font-mono font-bold text-emerald-700">{v.code}</span>
                  <p className="text-[10px] text-slate-500 mt-0.5">{v.client_name} — {v.redeem_center || v.center}</p>
                </div>
                <span className="text-[10px] text-slate-400 font-mono">{v.redeemed_at?.slice(0, 10)}</span>
              </div>
            )) : <p className="text-xs text-slate-400 text-center py-4">Sin canjes recientes</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
